/**
 * Connected-input measurement utilities.
 *
 * Used by the connected-input aspect-ratio warning in NodeAdapters: when an
 * image or video is wired into a generator, we measure what was actually
 * connected and compare it to the node's `aspectRatio` setting, so the user
 * finds out BEFORE paying for a generation that will be cropped or padded.
 *
 * `probeAudioDuration` below extends the same idea to BILLING rather than
 * framing: Kling Avatar's clip length is the connected audio's length, so the
 * number of seconds we charge for has to be measured off the input too.
 *
 * Kept in its own module (rather than inline in NodeAdapters) because these are
 * pure and worth testing directly — NodeAdapters pulls in the whole component
 * tree, CSS included, and cannot be imported from a plain script.
 */

/** Standard ratios we can name, so a warning can suggest what to switch to. */
export const NAMED_ASPECT_RATIOS: Array<{ label: string; ratio: number }> = [
  { label: '21:9', ratio: 21 / 9 },
  { label: '16:9', ratio: 16 / 9 },
  { label: '3:2', ratio: 3 / 2 },
  { label: '4:3', ratio: 4 / 3 },
  { label: '1:1', ratio: 1 },
  { label: '3:4', ratio: 3 / 4 },
  { label: '2:3', ratio: 2 / 3 },
  { label: '9:16', ratio: 9 / 16 },
];

/**
 * Parse an `aspectRatio` SETTING value into a numeric ratio.
 *
 * Returns null for anything that is not a fixed W:H ratio — 'auto', 'custom',
 * banner presets, undefined. null means "no fixed target", i.e. nothing to warn
 * about. The previous implementation collapsed every non-'16:9' value to 9:16,
 * so a node set to 1:1 was compared against 0.5625 and would have warned on
 * every square image.
 */
export function parseAspectRatioSetting(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const m = /^\s*(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)\s*$/.exec(value);
  if (!m) return null;
  const w = parseFloat(m[1]);
  const h = parseFloat(m[2]);
  if (!(w > 0) || !(h > 0)) return null;
  return w / h;
}

/** Closest named ratio to a measured one — for the "try X instead" hint. */
export function describeClosestRatio(ratio: number): string {
  let best = NAMED_ASPECT_RATIOS[0];
  let bestDiff = Infinity;
  for (const candidate of NAMED_ASPECT_RATIOS) {
    const diff = Math.abs(candidate.ratio - ratio);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = candidate;
    }
  }
  return best.label;
}

/**
 * How far apart two ratios are, measured in log space.
 *
 * Log space makes the tolerance SYMMETRIC: a 10% error reads the same whether
 * the input is wider or taller than the target. The old check used an absolute
 * difference against 0.1, which is ~6% at 16:9 but ~18% at 9:16 — so portrait
 * mismatches were under-detected and landscape ones over-detected.
 */
export function aspectRatioDrift(a: number, b: number): number {
  if (!(a > 0) || !(b > 0)) return Infinity;
  return Math.abs(Math.log(a / b));
}

/** Drift beyond which a mismatch is worth warning about (~10%). */
export const ASPECT_RATIO_DRIFT_TOLERANCE = 0.1;

// Measured ratios, keyed by URL. Module-level so every node on the canvas
// shares it and a re-render never re-downloads.
const mediaRatioCache = new Map<string, number | null>();

/**
 * Measure a media file's intrinsic aspect ratio.
 *
 * The old code did `const img = new Image(); img.src = url; if (img.complete)`
 * inside a useMemo. `complete` is false for anything not already decoded, and a
 * useMemo cannot await — so the check silently no-opped on virtually every
 * image. This resolves properly, handles video, and memoises per URL.
 *
 * Never rejects: an unreachable or malformed URL resolves to null.
 */
export function probeMediaRatio(url: string, kind: 'image' | 'video'): Promise<number | null> {
  const cached = mediaRatioCache.get(url);
  if (cached !== undefined) return Promise.resolve(cached);

  return new Promise<number | null>((resolve) => {
    const done = (ratio: number | null) => {
      mediaRatioCache.set(url, ratio);
      resolve(ratio);
    };
    // Don't hang forever on a dead CDN URL.
    const timeout = setTimeout(() => done(null), 10000);

    if (kind === 'video') {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        clearTimeout(timeout);
        done(video.videoWidth > 0 && video.videoHeight > 0 ? video.videoWidth / video.videoHeight : null);
      };
      video.onerror = () => { clearTimeout(timeout); done(null); };
      video.src = url;
      return;
    }

    const img = new Image();
    img.onload = () => {
      clearTimeout(timeout);
      done(img.naturalWidth > 0 && img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : null);
    };
    img.onerror = () => { clearTimeout(timeout); done(null); };
    img.src = url;
  });
}

// Measured durations in seconds, keyed by URL. Separate map from the ratio
// cache above because the two are probed independently and a URL may be in one
// and not the other.
const mediaDurationCache = new Map<string, number | null>();

/**
 * Measure a media file's duration in seconds.
 *
 * Exists because Kling Avatar v2 has NO duration parameter — the generated clip
 * is exactly as long as the audio you feed it. Billing from the node's own
 * setting therefore charges for a length the model never produces: a 30s track
 * billed as 5s costs us ~4x what we take. The number of seconds we reserve has
 * to come from the file.
 *
 * Same contract as probeMediaRatio: memoised per URL, resolves rather than
 * rejects, and gives up after a timeout so a dead CDN URL cannot hang a node.
 *
 * Returns null — never a guess — when the duration cannot be established.
 * Callers MUST treat null as "do not bill", not as "assume a default":
 *   • `duration` is NaN until metadata loads,
 *   • it is Infinity for streams and for some webm/ogg files with no seek
 *     index, which would otherwise turn into an infinite charge,
 *   • a cross-origin URL without CORS headers fires onerror.
 */
export function probeAudioDuration(url: string): Promise<number | null> {
  const cached = mediaDurationCache.get(url);
  if (cached !== undefined) return Promise.resolve(cached);

  return new Promise<number | null>((resolve) => {
    const done = (seconds: number | null) => {
      mediaDurationCache.set(url, seconds);
      resolve(seconds);
    };
    const timeout = setTimeout(() => done(null), 10000);

    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      clearTimeout(timeout);
      const seconds = audio.duration;
      // Reject NaN and Infinity explicitly: Number.isFinite covers both, and
      // both are values `duration` really returns rather than theoretical.
      done(Number.isFinite(seconds) && seconds > 0 ? seconds : null);
    };
    audio.onerror = () => { clearTimeout(timeout); done(null); };
    audio.src = url;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Auto-selection
//
// Detecting the connected input's ratio only ever produced a ⚠️. These helpers
// turn that reading into an actual setting change: pick the aspect-ratio option
// the node's dropdown is CURRENTLY offering that sits closest to the measured
// input.
//
// "Currently offering" is the whole difficulty. `aspectRatio` is a
// SettingsFieldConfig whose option list can be narrowed per selected model via
// `conditionalOptions` (NodeSettingsFooter resolves it that way when it renders
// the dropdown). Kling Image offers 3:2; Kling Video does not. Snapping a Kling
// Video node to 3:2 because the input photo is 3:2 would write a value the
// provider rejects. So the effective list is resolved the same way the footer
// resolves it, and only entries that parse as a real W:H ratio are candidates.
// ─────────────────────────────────────────────────────────────────────────────

/** One entry of a `select` field's option list. */
export interface AspectRatioOption {
  value: string;
  label?: string;
}

/**
 * The shape of a settings field this module needs. Declared structurally rather
 * than importing `SettingsFieldConfig`, so this module stays free of the React
 * component tree and remains importable from a plain test.
 */
export interface OptionFieldSpec {
  type?: string;
  options?: ReadonlyArray<AspectRatioOption>;
  conditionalOptions?: {
    dependsOn: string;
    rules: Record<string, ReadonlyArray<AspectRatioOption>>;
  };
}

/**
 * The option list the dropdown is actually showing right now.
 *
 * Mirrors NodeSettingsFooter's `displayOptions`: base `options`, replaced
 * wholesale by `conditionalOptions.rules[<dependent value>]` when a rule exists
 * for the current value of the field it depends on (in practice `selectedModel`
 * or `mode`). Only `select` fields honour conditionalOptions there, so the same
 * gate applies here.
 *
 * NOTE for callers: `selectedModel` lives at `node.data.selectedModel`, NOT in
 * `node.data.parameters`. Build `dependentValues` accordingly or the narrowing
 * silently no-ops.
 */
export function resolveOfferedOptions(
  field: OptionFieldSpec | undefined | null,
  dependentValues: Record<string, unknown> | undefined | null
): AspectRatioOption[] {
  if (!field) return [];
  let displayOptions: ReadonlyArray<AspectRatioOption> = field.options ?? [];

  if (field.conditionalOptions && (field.type === undefined || field.type === 'select')) {
    const { dependsOn, rules } = field.conditionalOptions;
    const dependentValue = dependentValues ? dependentValues[dependsOn] : undefined;
    if (dependentValue !== undefined && dependentValue !== null && dependentValue !== '') {
      const rule = rules?.[String(dependentValue)];
      if (rule) displayOptions = rule;
    }
  }

  return displayOptions.slice();
}

/**
 * The subset of an option list that names a real W:H ratio.
 *
 * Drops 'auto', 'custom', 'original', banner presets ('google-300x250'),
 * `image_size` enums ('square_hd', 'portrait_4_3') — anything
 * `parseAspectRatioSetting` cannot turn into a number. A node whose aspect
 * setting is made entirely of those has nothing to snap to.
 */
export function offeredRatioOptions(
  options: ReadonlyArray<AspectRatioOption | string> | undefined | null
): string[] {
  if (!options) return [];
  const out: string[] = [];
  for (const option of options) {
    const value = typeof option === 'string' ? option : option?.value;
    if (typeof value !== 'string') continue;
    if (parseAspectRatioSetting(value) === null) continue;
    out.push(value);
  }
  return out;
}

/**
 * The offered option closest to `ratio`, or null when none of them is a ratio.
 *
 * Closeness is `aspectRatioDrift` (log-space), so portrait and landscape are
 * judged on the same scale. Ties keep the earlier option, so the result is
 * stable for a given option order.
 */
export function chooseClosestOffered(
  ratio: number,
  options: ReadonlyArray<AspectRatioOption | string> | undefined | null
): string | null {
  if (!(ratio > 0) || !Number.isFinite(ratio)) return null;

  let best: string | null = null;
  let bestDrift = Infinity;
  for (const value of offeredRatioOptions(options)) {
    const candidate = parseAspectRatioSetting(value);
    if (candidate === null) continue;
    const drift = aspectRatioDrift(ratio, candidate);
    if (drift < bestDrift) {
      bestDrift = drift;
      best = value;
    }
  }
  return best;
}

export interface AspectRatioSnapInput {
  /** Measured ratio of the connected image/video, or null if nothing measured. */
  detectedRatio: number | null;
  /** What `parameters.aspectRatio` currently holds. */
  currentValue: unknown;
  /** The definition's default for the aspectRatio field, if it is a string. */
  defaultValue?: string;
  /** True once the user has chosen an aspect ratio on this node themselves. */
  manuallySet: boolean;
  /** True when the current value is one WE wrote on a previous snap. */
  autoSelected: boolean;
  /** The option list the dropdown is offering right now (see resolveOfferedOptions). */
  offered: ReadonlyArray<AspectRatioOption | string> | undefined | null;
}

/**
 * Decide what to write into `parameters.aspectRatio`, or null to write nothing.
 *
 * The rules, in the order they are applied:
 *
 *  1. No offered option parses as a W:H ratio (banner presets, `image_size`
 *     enums, an 'auto'-only list) — there is nothing legal to snap to. Warn,
 *     don't snap.
 *  2. The stored value is 'custom' — that means explicit width/height, which is
 *     where banner presets land. Never ours to move.
 *  3. The user chose the ratio themselves — never fight them.
 *  4. The value looks user-chosen even though no flag says so. Flows saved
 *     before this feature existed carry no flag, so a stored value that is
 *     neither the definition default nor one we wrote is treated as a real
 *     choice and left alone.
 *  5. Otherwise snap toward the connected input, or — when nothing is
 *     connected — toward the stored value's own ratio. That second target is a
 *     no-op while the stored value is still offered (it is its own closest
 *     match, and rule 6 swallows it) and a repair once a model switch has
 *     narrowed the dropdown out from under it, landing as near as possible to
 *     what was showing.
 *  6. Writing the value it already has is not a change. Return null so the
 *     caller does no setNodes and triggers no re-render.
 *
 * 'auto' is deliberately NOT preferred: it is not a parseable ratio, so it is
 * never a snap target, and a node sitting on 'auto' with a measured input snaps
 * to the explicit ratio.
 */
export function decideAspectRatioSnap(input: AspectRatioSnapInput): string | null {
  const { detectedRatio, currentValue, defaultValue, manuallySet, autoSelected, offered } = input;

  const ratioOptions = offeredRatioOptions(offered);
  if (ratioOptions.length === 0) return null;

  const current =
    typeof currentValue === 'string' && currentValue.length > 0 ? currentValue : null;

  if (current === 'custom') return null;
  if (manuallySet) return null;

  const untouched = autoSelected || current === null || current === defaultValue;
  if (!untouched) return null;

  // Nothing connected? Fall back to the stored value's own ratio. That is a
  // no-op whenever the value is still offered (it is its own closest match, and
  // the idempotency check below swallows it), and a repair when a model switch
  // narrowed the dropdown out from under it.
  const target =
    detectedRatio !== null && detectedRatio > 0
      ? detectedRatio
      : parseAspectRatioSetting(current);
  if (target === null) return null;

  const best = chooseClosestOffered(target, ratioOptions);
  if (best === null || best === current) return null;
  return best;
}
